import { useEffect, useMemo, useState } from 'react'
import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { Link, useNavigate } from 'react-router-dom'
import { useDashboard, type SaudeFinanceira } from '@hooks/useDashboard.js'
import { useAlertas } from '@hooks/useAlertas.js'
import { useProximoPasso } from '@hooks/useProximoPasso.js'
import { useHojeNaObra } from '@hooks/useHojeNaObra.js'
import { useModeStore } from '@stores/modeStore.js'
import { Badge } from '@components/primitives/Badge.js'
import { Button } from '@components/primitives/Button.js'
import { Money } from '@components/primitives/Money.js'
import { NavIcon } from '@components/layout/NavIcons.js'
import { formatCurrency, formatDate } from '@utils/format.js'
import {
  ROUTES,
  FASES_CONSTRUTIVAS,
  RESERVA_CONTINGENCIA_PADRAO_PCT,
  contratoDetailPath,
  topicoDetailPath,
} from '@utils/constants.js'
import { precisaBackup } from '@services/backup.js'
import { rotaDoAlerta } from '@services/alertasService.js'
import type { AgendaItem, SemanaAcao } from '@services/gestao.js'
import { barFillStyle } from '@utils/barStyle.js'
import { isModoHintSeen, markModeExplicit, markModoHintSeen } from '@utils/modePreference.js'
import { PageShell } from '@components/layout/PageShell.js'
import { CaixaMesStrip } from '@components/obra/CaixaMesStrip.js'

const SAUDE_LABEL: Record<SaudeFinanceira, string> = {
  ok: 'Saudável',
  atencao: 'Atenção',
  critico: 'Crítico',
}

const FOCO_FASE: Record<string, string> = {
  'pre-obra': 'Alvará, projetos compatibilizados e orçamento fechado por fase.',
  infraestrutura: 'Conferir armações, concretagens e impermeabilização de baldrames.',
  superestrutura: 'Validar projeto estrutural a cada etapa e registrar não conformidades.',
  vedacao: 'Prumo, nível e reservas para instalações antes de fechar alvenaria.',
  cobertura: 'Impermeabilizar antes de telhar e registrar fotos das camadas.',
  instalacoes: 'Fotografar tubulações e caixas antes de fechar paredes.',
  'acabamento-bruto': 'Respeitar cura do reboco (28 dias) antes da massa corrida.',
  'acabamento-fino': 'Comparar orçamentos e registrar escolhas de materiais.',
  entrega: 'Checklist de entrega, Habite-se e documentação de garantia.',
}

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

function quandoLabel(item: AgendaItem): { topo: string; base: string } {
  if (item.emDias < 0) return { topo: `${Math.abs(item.emDias)}d`, base: 'atrás' }
  if (item.emDias === 0) return { topo: 'Hoje', base: '' }
  if (item.emDias === 1) return { topo: 'Amanhã', base: '' }
  const d = new Date(`${item.data}T12:00:00`)
  return { topo: DIAS_SEMANA[d.getDay()] ?? '', base: String(d.getDate()).padStart(2, '0') }
}

function dataHojeLonga(): string {
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date())
}

function quandoSemana(item: SemanaAcao): string {
  if (item.emDias == null) return 'Sem data'
  if (item.emDias < 0) return `${Math.abs(item.emDias)}d atrasado`
  if (item.emDias === 0) return 'Hoje'
  if (item.emDias === 1) return 'Amanhã'
  return `Em ${item.emDias}d`
}

export function ObraCommandPage() {
  const dash = useDashboard()
  const hoje = useHojeNaObra(7)
  const { alertasAtivos } = useAlertas()
  const proximo = useProximoPasso()
  const navigate = useNavigate()
  const setMode = useModeStore((s) => s.setMode)
  const [mostrarBackup, setMostrarBackup] = useState(false)
  const [showModoHint, setShowModoHint] = useState(() => !isModoHintSeen())

  const config = dash.config
  const faseId = hoje.cronograma.faseAtualId ?? config?.faseConstrutiva ?? null
  const fase = FASES_CONSTRUTIVAS.find((f) => f.id === faseId)
  const foco = faseId ? FOCO_FASE[faseId] : undefined
  const emPreObra = !config?.dataInicio || faseId === 'pre-obra' || faseId === null

  const orcamento = config?.orcamentoTotal ?? 0
  const reservaPct = config?.reservaContingenciaPct ?? RESERVA_CONTINGENCIA_PADRAO_PCT
  const reserva = useMemo(() => {
    if (orcamento <= 0) return null
    const valorReserva = Math.round(orcamento * reservaPct) / 100
    const limiteSemReserva = orcamento - valorReserva
    const consumida = dash.totalGasto > limiteSemReserva
    const restante = Math.max(orcamento - dash.totalGasto, 0)
    return { valorReserva, consumida, restante, intacta: Math.min(restante, valorReserva) }
  }, [orcamento, reservaPct, dash.totalGasto])

  useEffect(() => {
    setMostrarBackup(precisaBackup())
  }, [])

  const dismissModoHint = () => {
    markModoHintSeen()
    setShowModoHint(false)
  }

  const semana = hoje.semana
  const mostraSemana = semana.aMedir.length > 0 || semana.aPagar.length > 0 || semana.prova !== null
  const idsParcelaSemana = new Set([
    ...semana.aMedir.map((i) => `parcela-${i.parcelaId}`),
    ...semana.aPagar.map((i) => `parcela-${i.parcelaId}`),
  ])
  const agendaExtra = hoje.agenda.filter((a) => !idsParcelaSemana.has(a.id))
  const atrasados = agendaExtra.filter((a) => a.emDias < 0).length
  const mostraAgenda = agendaExtra.length > 0

  const blocoGuia = proximo && (
    <div className='cf3-obra-guia cf3-obra-guia--v2'>
      <div className='cf3-obra-guia__body'>
        <p className='cf3-overline'>{GUIA_LABELS.etapaGuiaEmAndamento}</p>
        <h2 className='cf3-obra-guia__title'>{proximo.topicoTitulo}</h2>
        <p className='cf3-obra-guia__modulo'>{proximo.moduloTitulo}</p>
      </div>
      <div className='cf3-actions cf3-actions--row'>
        <Button
          variant='primary'
          onClick={() => {
            markModeExplicit()
            setMode('guia')
            navigate(topicoDetailPath(proximo.moduloId, proximo.topicoId))
          }}
        >
          Continuar no guia
        </Button>
        <Button variant='secondary' onClick={() => navigate(ROUTES.ETAPA)}>
          {GUIA_LABELS.resumoDaEtapa}
        </Button>
      </div>
    </div>
  )

  return (
    <PageShell variant='obra-hero'>
      {mostrarBackup && !showModoHint && (
        <div className='cf3-backup-nudge' role='status'>
          <p className='cf3-backup-nudge__text'>
            Seus dados ficam só neste aparelho. Exporte um backup para não perder registros.
          </p>
          <Button variant='secondary' size='sm' onClick={() => navigate(ROUTES.CONFIG)}>
            Fazer backup
          </Button>
        </div>
      )}

      {showModoHint && (
        <div className='cf3-modo-hint' role='status'>
          <p className='cf3-modo-hint__text'>
            <strong>Guia</strong>: o que decidir em cada etapa. <strong>Obra</strong>: gastos,
            contratos, cronograma e o dia a dia.
          </p>
          <button type='button' className='cf3-modo-hint__btn' onClick={dismissModoHint}>
            Entendi
          </button>
        </div>
      )}

      <section className='cf3-obra-hero' aria-label='Hoje na obra'>
        <p className='cf3-obra-hero__saudacao'>{dataHojeLonga()}</p>
        <h1 className='cf3-obra-hero__nome'>{config?.nome ?? 'Minha obra'}</h1>
        <div className='cf3-hoje-stats'>
          <div className='cf3-hoje-stat cf3-hoje-stat--fase'>
            <span className='cf3-hoje-stat__label'>Fase</span>
            <span className='cf3-hoje-stat__value'>{fase?.label ?? 'Não definida'}</span>
          </div>
          <div className='cf3-hoje-stat'>
            <span className='cf3-hoje-stat__label'>Avanço</span>
            <span className='cf3-hoje-stat__value'>{hoje.cronograma.avancoGeral}%</span>
          </div>
          <div className='cf3-hoje-stat'>
            <span className='cf3-hoje-stat__label'>
              {hoje.cronograma.atrasoDias > 0 ? 'Atraso' : 'Dia de obra'}
            </span>
            <span className='cf3-hoje-stat__value'>
              {hoje.cronograma.atrasoDias > 0
                ? `${hoje.cronograma.atrasoDias} dias`
                : hoje.cronograma.diasDeObra !== null
                  ? String(hoje.cronograma.diasDeObra)
                  : 'a definir'}
            </span>
          </div>
        </div>
        {hoje.cronograma.terminoPrevisto && (
          <p className='cf3-hoje-stats__foot'>
            Término previsto {formatDate(hoje.cronograma.terminoPrevisto)}
          </p>
        )}
      </section>

      <section className='cf3-block' aria-label='Ações rápidas'>
        <div className='cf3-acoes-rapidas'>
          <button
            type='button'
            className='cf3-acao-rapida'
            onClick={() =>
              navigate(ROUTES.FINANCEIRO, { state: { prefillLancamento: { tipo: 'gasto' } } })
            }
          >
            <NavIcon icon='financeiro' size={22} />
            <span>Gasto</span>
          </button>
          <Link className='cf3-acao-rapida' to={`${ROUTES.DIARIO}?foto=1`}>
            <NavIcon icon='camera' size={22} />
            <span>Foto</span>
          </Link>
          <Link className='cf3-acao-rapida' to={ROUTES.DIARIO}>
            <NavIcon icon='diario' size={22} />
            <span>Anotação</span>
          </Link>
          <Link className='cf3-acao-rapida' to={ROUTES.PENDENCIAS}>
            <NavIcon icon='pendencias' size={22} />
            <span>Tarefa</span>
          </Link>
        </div>
      </section>

      {hoje.ritmo.parado && (
        <div className='cf3-ritmo' role='status'>
          <p className='cf3-ritmo__text'>
            {hoje.ritmo.diasSemDiario == null
              ? 'Obra sem nenhuma anotação no diário. Sem registro, não há prova.'
              : `Obra sem registro há ${hoje.ritmo.diasSemDiario} dias.`}
            {hoje.ritmo.chuvasNaSemana > 0
              ? ` ${hoje.ritmo.chuvasNaSemana} dia${hoje.ritmo.chuvasNaSemana > 1 ? 's' : ''} de chuva nesta semana.`
              : ''}
          </p>
          <Link className='cf3-hoje__link' to={ROUTES.DIARIO}>
            Abrir diário
          </Link>
        </div>
      )}

      {mostraSemana && (
        <section className='cf3-block' aria-labelledby='semana-heading'>
          <div className='cf3-gestao-section-head'>
            <h2 id='semana-heading' className='cf3-subsection-title'>
              Esta semana
            </h2>
          </div>
          <div className='cf3-block__card cf3-semana'>
            {(semana.aMedir.length > 0 || semana.aPagar.length > 0) && (
              <div className='cf3-semana__kpis'>
                <div className='cf3-semana__kpi'>
                  <span className='cf3-semana__kpi-label'>Pode sair da conta</span>
                  <strong>{formatCurrency(semana.saidaLiberada)}</strong>
                </div>
                <div className='cf3-semana__kpi cf3-semana__kpi--hold'>
                  <span className='cf3-semana__kpi-label'>Não pague ainda</span>
                  <strong>{formatCurrency(semana.valorSemMedir)}</strong>
                </div>
              </div>
            )}

            {semana.aMedir.length > 0 && (
              <div className='cf3-semana__lista'>
                <p className='cf3-semana__lista-title'>Meça no canteiro antes de pagar</p>
                <ul>
                  {semana.aMedir.slice(0, 4).map((item) => (
                    <li key={`medir-${item.parcelaId}`}>
                      <Link className='cf3-semana__item' to={contratoDetailPath(item.contratoId)}>
                        <span className='cf3-semana__item-texto'>
                          <span className='cf3-semana__item-titulo'>
                            {item.prestador} · {item.descricao}
                          </span>
                          <span className='cf3-semana__item-meta'>
                            {quandoSemana(item)} · {formatCurrency(item.valor)}
                          </span>
                        </span>
                        <span className='cf3-semana__item-acao'>Medir</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {semana.aPagar.length > 0 && (
              <div className='cf3-semana__lista'>
                <p className='cf3-semana__lista-title'>Liberado ou sinal: pode pagar</p>
                <ul>
                  {semana.aPagar.slice(0, 4).map((item) => (
                    <li key={`pagar-${item.parcelaId}`}>
                      <Link className='cf3-semana__item' to={contratoDetailPath(item.contratoId)}>
                        <span className='cf3-semana__item-texto'>
                          <span className='cf3-semana__item-titulo'>
                            {item.prestador} · {item.descricao}
                          </span>
                          <span className='cf3-semana__item-meta'>
                            {quandoSemana(item)} · {formatCurrency(item.valor)}
                          </span>
                        </span>
                        <span className='cf3-semana__item-acao cf3-semana__item-acao--pagar'>
                          Pagar
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {semana.prova && (
              <div className='cf3-semana__prova'>
                <p className='cf3-semana__prova-title'>Prova da fase · {semana.prova.label}</p>
                {foco && <p className='cf3-semana__prova-dica'>{foco}</p>}
                <p className='cf3-semana__prova-dica'>{semana.prova.dica}</p>
                <p className='cf3-semana__prova-status'>
                  {semana.fotosNaSemana === 0
                    ? 'Nenhuma foto no diário nos últimos 7 dias.'
                    : `${semana.fotosNaSemana} foto${semana.fotosNaSemana > 1 ? 's' : ''} no diário nesta semana.`}
                </p>
                <Link className='cf3-hoje__link' to={`${ROUTES.DIARIO}?foto=1`}>
                  {semana.fotosNaSemana === 0 ? 'Registrar foto' : 'Ver diário'}
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {emPreObra && blocoGuia}

      {mostraAgenda && (
        <section className='cf3-block' aria-labelledby='agenda-heading'>
          <div className='cf3-gestao-section-head'>
            <h2 id='agenda-heading' className='cf3-subsection-title'>
              Próximos 7 dias
            </h2>
            {atrasados > 0 && (
              <Badge
                variant='danger'
                size='sm'
                label={`${atrasados} atrasado${atrasados > 1 ? 's' : ''}`}
              />
            )}
          </div>
          <div className='cf3-block__card'>
            <ul className='cf3-agenda'>
              {agendaExtra.slice(0, 6).map((item) => {
                const q = quandoLabel(item)
                return (
                  <li key={item.id}>
                    <Link className='cf3-agenda__item' to={item.rota}>
                      <span
                        className={`cf3-agenda__quando${item.emDias < 0 ? ' cf3-agenda__quando--atrasado' : item.emDias === 0 ? ' cf3-agenda__quando--hoje' : ''}`}
                      >
                        <span>{q.topo}</span>
                        {q.base && <span>{q.base}</span>}
                      </span>
                      <span className='cf3-agenda__texto'>
                        <span className='cf3-agenda__titulo'>{item.titulo}</span>
                        <span className='cf3-agenda__detalhe'>
                          {item.detalhe}
                          {item.valor ? ` · ${formatCurrency(item.valor)}` : ''}
                        </span>
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        </section>
      )}

      {!mostraAgenda && hoje.pendenciasAbertas > 0 && (
        <p className='cf3-hoje__linha'>
          <Link className='cf3-hoje__link' to={ROUTES.PENDENCIAS}>
            {hoje.pendenciasAbertas} tarefa{hoje.pendenciasAbertas > 1 ? 's' : ''} sem prazo
          </Link>
        </p>
      )}

      {fase && !semana.prova && (
        <section className='cf3-block' aria-labelledby='fase-heading'>
          <div className='cf3-gestao-section-head'>
            <h2 id='fase-heading' className='cf3-subsection-title'>
              Foco agora
            </h2>
            <Link className='cf3-hoje__link' to={ROUTES.CRONOGRAMA}>
              Cronograma
            </Link>
          </div>
          <div className='cf3-block__card cf3-block__card--hint'>
            <p className='cf3-hoje__linha'>
              <strong>{fase.label}.</strong> {foco ?? fase.desc}
            </p>
          </div>
        </section>
      )}

      {hoje.decisoesTravando.length > 0 && (
        <section className='cf3-block' aria-labelledby='decisoes-heading'>
          <div className='cf3-gestao-section-head'>
            <h2 id='decisoes-heading' className='cf3-subsection-title'>
              Decisões que travam agora
            </h2>
            <Link className='cf3-hoje__link' to={ROUTES.DECISOES}>
              Memorial
            </Link>
          </div>
          <ul className='cf3-decisao-lista'>
            {hoje.decisoesTravando.slice(0, 3).map((c) => (
              <li key={c.chave}>
                <Link
                  className='cf3-decisao cf3-decisao--pendente'
                  to={`${ROUTES.DECISOES}?chave=${encodeURIComponent(c.chave)}`}
                >
                  <span className='cf3-decisao__titulo'>{c.titulo}</span>
                  <span className='cf3-decisao__motivo'>{c.motivo}</span>
                  <span className='cf3-decisao__acao'>Registrar</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {orcamento > 0 && (
        <section className='cf3-block' aria-labelledby='fin-heading'>
          <div className='cf3-gestao-section-head'>
            <h2 id='fin-heading' className='cf3-subsection-title'>
              Saúde financeira
            </h2>
            <Link className='cf3-hoje__link' to={ROUTES.FINANCEIRO}>
              Financeiro
            </Link>
          </div>
          <div className='cf3-block__card'>
            <div className='cf3-fin-dash'>
              <div className='cf3-fin-dash__head'>
                <div>
                  <Money value={dash.saldo} size={24} ariaLabel='Saldo da obra' />
                </div>
                <Badge
                  variant={
                    dash.pctUsado >= 85 ? 'danger' : dash.pctUsado >= 60 ? 'warning' : 'success'
                  }
                  label={`${dash.pctUsado}% usado`}
                />
              </div>
              <div
                className='cf3-fin-budget-bar'
                role='progressbar'
                aria-valuenow={dash.pctUsado}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className={`cf3-fin-budget-bar__fill${dash.pctUsado >= 90 ? ' cf3-fin-budget-bar__fill--danger' : dash.pctUsado >= 70 ? ' cf3-fin-budget-bar__fill--warning' : ''}`}
                  style={barFillStyle(Math.min(dash.pctUsado, 100))}
                />
              </div>
              <div className='cf3-fin-saldo-footer'>
                <span>
                  {formatCurrency(dash.totalGasto)} de {formatCurrency(orcamento)}
                </span>
                <span className={`cf3-fin-saude cf3-fin-saude--${dash.saudeFinanceira}`}>
                  {SAUDE_LABEL[dash.saudeFinanceira]}
                </span>
              </div>
              {reserva && (
                <p className={`cf3-reserva${reserva.consumida ? ' cf3-reserva--consumida' : ''}`}>
                  <span>Reserva para imprevistos ({reservaPct}%)</span>
                  <strong>
                    {reserva.consumida
                      ? `${formatCurrency(reserva.intacta)} de ${formatCurrency(reserva.valorReserva)}`
                      : formatCurrency(reserva.valorReserva)}
                  </strong>
                </p>
              )}
              <CaixaMesStrip caixa={hoje.caixa} compact />
            </div>
          </div>
        </section>
      )}

      {orcamento <= 0 && (hoje.caixa.saidaLiberada > 0 || hoje.caixa.saidaSemMedir > 0) && (
        <section className='cf3-block' aria-label='Caixa do mês'>
          <div className='cf3-block__card'>
            <CaixaMesStrip caixa={hoje.caixa} />
          </div>
        </section>
      )}

      {!emPreObra && blocoGuia}

      {alertasAtivos.length > 0 && (
        <section className='cf3-block' aria-labelledby='alertas-heading'>
          <h2 id='alertas-heading' className='cf3-subsection-title'>
            Alertas ({alertasAtivos.length})
          </h2>
          {alertasAtivos.slice(0, 3).map((a) => {
            const rota = rotaDoAlerta(a.chave)
            const texto = a.titulo
            return rota ? (
              <Link key={a.id} className='cf3-obra-alerta cf3-obra-alerta--link' to={rota}>
                {texto}
              </Link>
            ) : (
              <div key={a.id} className='cf3-obra-alerta'>
                {texto}
              </div>
            )
          })}
          <Button variant='ghost' size='sm' onClick={() => navigate(ROUTES.ALERTAS)}>
            Ver todos
          </Button>
        </section>
      )}

      {dash.atividadeRecente.length > 0 && (
        <section className='cf3-block' aria-labelledby='atividade-heading'>
          <h2 id='atividade-heading' className='cf3-subsection-title'>
            Atividade recente
          </h2>
          <ul className='cf3-atividade'>
            {dash.atividadeRecente.slice(0, 5).map((item, i) => (
              <li key={i} className='cf3-atividade-card'>
                <div className='cf3-atividade-card__main'>
                  <span className='cf3-atividade-card__ctx'>{item.contexto}</span>
                  <p className='cf3-atividade-card__desc'>{item.descricao}</p>
                </div>
                <time className='cf3-atividade-card__date' dateTime={item.data}>
                  {formatDate(item.data)}
                </time>
              </li>
            ))}
          </ul>
        </section>
      )}
    </PageShell>
  )
}
